/**
 * Background runner that drains the offline queue.
 *
 * Strategy:
 *   - On 'online' event and on a 30s interval, walk pending ops with
 *     nextAttemptAt <= now, pull their handler from the registry, run it.
 *   - Exponential backoff on retryable failures: 5s, 15s, 45s, 2m, 6m, 18m
 *     (capped). Cap attempts at 8 — beyond that, the op is marked 'failed'
 *     and surfaced in /field/sync for the user to retry manually or cancel.
 *   - Non-retryable failures (e.g. 4xx validation): mark 'failed' immediately.
 *   - 'in_flight' guards prevent two runners from racing the same op if the
 *     interval and the online event fire at once.
 *
 * Public API: start() once at app boot. The runner self-schedules.
 */

import { notifications } from '../../../config/toast-notifications.js'

import { getHandler } from './registry.js'
import { getOfflineQueueStore } from './store.js'
import type { QueuedOp } from './types.js'

const BACKOFF_SCHEDULE_MS = [5_000, 15_000, 45_000, 120_000, 360_000, 1_080_000]
const MAX_ATTEMPTS = 8
const TICK_INTERVAL_MS = 30_000

let started = false
let tickHandle: number | null = null
let runningPromise: Promise<void> | null = null

function nextBackoff(attempts: number): number {
  const idx = Math.min(attempts, BACKOFF_SCHEDULE_MS.length - 1)
  return BACKOFF_SCHEDULE_MS[idx]
}

/**
 * Mark an op terminally failed AND tell the user, so a hard failure never
 * silently looks like success. Before card 4_x_b5Smhsld, an iPhone photo whose
 * upload 400'd (unsupported HEIC) died here with no user-visible signal — the
 * capture UI had already toasted "Photos queued". Now every terminal failure
 * raises a persistent error toast pointing at /field/sync (where the op can be
 * retried or cancelled). Persistent so it isn't missed on a jobsite.
 */
async function failOp(op: QueuedOp, error: string): Promise<void> {
  const store = getOfflineQueueStore()
  await store.update(op.id, { status: 'failed', lastError: error })
  try {
    notifications.error(
      "A change didn't sync",
      `${op.description} failed to reach the server. Open Sync to retry or cancel it.`,
      true,
    )
  } catch {
    /* notifications not initialized (e.g. tests) — the /field/sync surface still shows it */
  }
}

async function runOnce(): Promise<void> {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return
  const store = getOfflineQueueStore()
  const ops = await store.list()
  const now = Date.now()
  const due = ops
    .filter((op) => op.status === 'pending' && op.nextAttemptAt <= now)
    .sort((a, b) => a.enqueuedAt - b.enqueuedAt)

  for (const op of due) {
    await runOp(op)
    if (typeof navigator !== 'undefined' && !navigator.onLine) break
  }
}

async function runOp(op: QueuedOp): Promise<void> {
  const store = getOfflineQueueStore()
  const handler = getHandler(op.operation)
  if (!handler) {
    await failOp(op, `No handler registered for operation "${op.operation}"`)
    return
  }

  await store.update(op.id, { status: 'in_flight', attempts: op.attempts + 1 })

  try {
    const result = await handler(op)
    if (result.ok) {
      await store.remove(op.id)
      return
    }
    if (!result.retryable || op.attempts + 1 >= MAX_ATTEMPTS) {
      await failOp(op, result.error)
      return
    }
    await store.update(op.id, {
      status: 'pending',
      lastError: result.error,
      nextAttemptAt: Date.now() + nextBackoff(op.attempts + 1),
    })
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err)
    if (op.attempts + 1 >= MAX_ATTEMPTS) {
      await failOp(op, message)
      return
    }
    await store.update(op.id, {
      status: 'pending',
      lastError: message,
      nextAttemptAt: Date.now() + nextBackoff(op.attempts + 1),
    })
  }
}

async function tick() {
  if (runningPromise) return
  runningPromise = runOnce().finally(() => {
    runningPromise = null
  })
  await runningPromise
}

/** Trigger a queue drain right now (no waiting for the next interval). */
export async function flushQueue(): Promise<void> {
  await tick()
}

/** Reset a failed op so the runner picks it up again. Used by /field/sync. */
export async function retryOp(opId: string): Promise<void> {
  const store = getOfflineQueueStore()
  const op = await store.getById(opId)
  if (!op) return
  await store.update(opId, {
    status: 'pending',
    nextAttemptAt: Date.now(),
    lastError: null,
  })
  void tick()
}

/** Start the runner. Safe to call multiple times. */
export function startOfflineQueueRunner() {
  if (started) return
  started = true

  if (typeof window === 'undefined') return

  // Drain whenever we come back online.
  window.addEventListener('online', () => {
    void tick()
  })

  // Periodic tick for retries that are due based on backoff.
  tickHandle = window.setInterval(() => {
    void tick()
  }, TICK_INTERVAL_MS)

  // Initial tick on boot in case there's already pending work.
  void tick()
}

/** Stop the runner — primarily for tests. */
export function stopOfflineQueueRunner() {
  started = false
  if (tickHandle != null) {
    clearInterval(tickHandle)
    tickHandle = null
  }
}
